import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import SEO from "../components/SEO";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}!`);
      // Only honour same-origin /app/* paths; admin paths require admin role
      const safePath = next && /^\/app\//.test(next) && !next.includes("//") ? next : null;
      if (safePath && safePath.includes("/app/admin") && u.role === "admin") {
        nav(safePath);
      } else {
        nav("/app");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] grid-pattern px-6">
      <SEO title="Sign In" description="Sign in to Project Wingman and continue your AI-powered interview preparation." path="/login" noIndex />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          <img src="/wingman-logo.png" alt="Wingman.AI" className="h-8 w-auto" />
          <span className="font-display font-bold tracking-tight text-lg">WINGMAN<span className="text-[#D3FF24]">.</span>AI</span>
        </Link>
        <div className="border border-white/10 bg-[#0F0F0F] p-8">
          <h1 className="font-display text-3xl font-bold tracking-tight mb-1">Welcome back</h1>
          <p className="text-sm text-neutral-500 mb-8">Sign in to Project Wingman.</p>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Email</label>
              <input
                data-testid="login-email"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Password</label>
              <input
                data-testid="login-password"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none"
              />
            </div>
            <button
              data-testid="login-submit"
              disabled={busy}
              className="w-full bg-[#D3FF24] text-black py-3 text-xs uppercase tracking-widest font-semibold hover:brightness-105 transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Sign in
            </button>
          </form>
          <div className="mt-6 text-sm text-neutral-500 text-center">
            New here? <Link to="/register" className="text-white hover:text-[#D3FF24]">Create account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
